import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import { useReportService, useInvalidateReportService } from '../../../hooks/queries/useReportService';
import ExperianOutputComponent from '../output/ExperianOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';

// Experian flow has 3 steps:
//   STEP 1: Collect first_name + last_name + mobile → POST generate-sdk-url
//   STEP 2: User opens the returned SDK URL in a new tab and completes verification
//   STEP 3: User clicks "Fetch Report" → POST credit-report with business_user_id

const STEPS = { FORM: 'form', SDK: 'sdk', REPORT: 'report' };
const NAME_REGEX = /^[A-Za-z\s]+$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;

const ExperianInputComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [step, setStep] = useState(STEPS.FORM);
    const [formData, setFormData] = useState({ first_name: '', last_name: '', mobile: '', consent: false });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [sdkUrl, setSdkUrl] = useState('');
    const [businessUserId, setBusinessUserId] = useState('');
    const [reportData, setReportData] = useState(null);
    // #161: one shared, cached query instead of a per-component fetch on every
    // mount. Concurrent consumers are deduped into a single request and a
    // revisit is served from cache; the balance is invalidated after a report
    // runs (below), which is what keeps a cached credit figure correct.
    const {
        data: fetchedServiceDetails,
        isFetching: fetchingService,
        isError: serviceError,
    } = useReportService(serviceId);
    const invalidateReportService = useInvalidateReportService();
    const serviceDetails = serviceError
        ? { available_credits: 'N/A', credits_required: 'N/A' }
        : (fetchedServiceDetails || null);

    const handleField = (field, transform = (v) => v) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: transform(e.target.value || '') }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const validate = () => {
        const next = {};
        if (!formData.first_name.trim()) next.first_name = 'First name is required';
        else if (!NAME_REGEX.test(formData.first_name)) next.first_name = 'Letters and spaces only';
        if (!formData.last_name.trim()) next.last_name = 'Last name is required';
        else if (!NAME_REGEX.test(formData.last_name)) next.last_name = 'Letters and spaces only';
        if (!formData.mobile) next.mobile = 'Mobile is required';
        else if (!MOBILE_REGEX.test(formData.mobile)) next.mobile = 'Enter a valid 10-digit Indian mobile';
        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const generateSdk = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/boostmoney/bureau/generate-sdk-url', {
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim(),
                mobile: formData.mobile,
                api_provider_service_id: parseInt(serviceId, 10),
            });
            if (res.data.status === 'success' || res.data.success === true) {
                const data = res.data.data || res.data;
                const url = data.sdk_url || data.sdkUrl || data.url;
                const buid = data.business_user_id || data.businessUserId;
                if (!url) throw new Error('SDK URL not received');
                setSdkUrl(url);
                setBusinessUserId(buid || '');
                if (buid) sessionStorage.setItem('boostmoney_business_user_id', buid);
                setStep(STEPS.SDK);
                toast.current?.show({ severity: 'success', summary: 'SDK ready', detail: 'Open the verification link below', life: 2500 });
            } else {
                throw new Error(res.data.message || 'Failed to generate SDK URL');
            }
        } catch (error) {
            console.error('Experian SDK URL generation failed:', error?.message || 'Unknown error');
            toast.current?.show({ severity: 'error', summary: 'SDK generation failed', detail: error.response?.data?.message || error.message || 'Failed to generate SDK URL', life: 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Name:</span> <span className="text-900 font-medium">{formData.first_name} {formData.last_name}</span></div>
                    <div className="mb-2"><span className="text-600">Mobile:</span> <span className="text-900 font-medium">{formData.mobile}</span></div>
                    <div className="mb-2 text-700">You will be redirected to the Experian verification SDK in the next step.</div>
                </div>
            ),
            header: 'Confirm Experian verification',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, proceed',
            rejectLabel: 'Cancel',
            accept: generateSdk,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '500px' },
            draggable: false,
        });
    };

    const fetchReport = async () => {
        const buid = businessUserId || sessionStorage.getItem('boostmoney_business_user_id');
        if (!buid) {
            toast.current?.show({ severity: 'warn', summary: 'Verification incomplete', detail: 'No business user ID found. Please re-run verification.', life: 3500 });
            return;
        }
        setLoading(true);
        try {
            const leadIdMatch = buid.match(/LEAD_(\d+)_/);
            const leadId = leadIdMatch ? parseInt(leadIdMatch[1], 10) : 1;
            const res = await axiosInstance.post('/boostmoney/bureau/credit-report', {
                lead_id: leadId,
                business_user_id: buid,
                api_provider_service_id: parseInt(serviceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Failed to fetch credit report');
            setReportData(res.data);
            setStep(STEPS.REPORT);
            toast.current?.show({ severity: 'success', summary: 'Experian report fetched', detail: 'Report ready below', life: 2000 });
            // Credits just changed. Invalidating updates every mounted consumer at
            // once and is ordered strictly after the deduction.
            invalidateReportService(serviceId);
        } catch (error) {
            console.error('Experian fetch report failed:', error?.message || 'Unknown error');
            toast.current?.show({ severity: 'error', summary: 'Report fetch failed', detail: error.response?.data?.message || error.message || 'Failed to fetch credit report', life: 3500 });
        } finally {
            setLoading(false);
        }
    };

    const handleAnother = () => {
        setStep(STEPS.FORM);
        setFormData({ first_name: '', last_name: '', mobile: '', consent: false });
        setErrors({});
        setSdkUrl('');
        setBusinessUserId('');
        setReportData(null);
        sessionStorage.removeItem('boostmoney_business_user_id');
    };

    const fieldClass = (f) => classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors[f] });
    const errorTip = (f, helpId) => errors[f] && (
        <small id={helpId} className="p-error block mt-1" role="alert">
            <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />{errors[f]}
        </small>
    );

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-chart-line"
                        title="Experian Credit Report"
                        subtitle="Fetch comprehensive credit report from Experian via Boostmoney"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />

                    {step === STEPS.FORM && (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    <div className="field col-12 md:col-6">
                                        <label htmlFor="exp-first" className="block font-medium text-sm mb-2">First Name <span className="text-red-500">*</span></label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-user" />
                                            <InputText id="exp-first" value={formData.first_name} onChange={handleField('first_name')}
                                                placeholder="Enter first name" maxLength={50} autoCapitalize="words"
                                                disabled={loading} className={fieldClass('first_name')} aria-invalid={!!errors.first_name} aria-describedby="exp-first-help" />
                                        </IconField>
                                        {errorTip('first_name', 'exp-first-help')}
                                    </div>
                                    <div className="field col-12 md:col-6">
                                        <label htmlFor="exp-last" className="block font-medium text-sm mb-2">Last Name <span className="text-red-500">*</span></label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-user" />
                                            <InputText id="exp-last" value={formData.last_name} onChange={handleField('last_name')}
                                                placeholder="Enter last name" maxLength={50} autoCapitalize="words"
                                                disabled={loading} className={fieldClass('last_name')} aria-invalid={!!errors.last_name} aria-describedby="exp-last-help" />
                                        </IconField>
                                        {errorTip('last_name', 'exp-last-help')}
                                    </div>
                                    <div className="field col-12 md:col-6 lg:col-4">
                                        <label htmlFor="exp-mobile" className="block font-medium text-sm mb-2">Mobile Number <span className="text-red-500">*</span></label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-phone" />
                                            <InputText id="exp-mobile" value={formData.mobile} onChange={handleField('mobile', (v) => v.replace(/\D/g, '').slice(0, 10))}
                                                placeholder="9876543210" maxLength={10} inputMode="tel" autoComplete="tel"
                                                disabled={loading} className={classNames(fieldClass('mobile'), 'font-mono')} aria-invalid={!!errors.mobile} aria-describedby="exp-mobile-help" />
                                        </IconField>
                                        {errorTip('mobile', 'exp-mobile-help')}
                                    </div>
                                </div>
                                <ConsentCheckbox
                                    checked={formData.consent}
                                    onChange={(e) => { setFormData((p) => ({ ...p, consent: e.checked })); setErrors((p) => ({ ...p, consent: '' })); }}
                                    error={errors.consent}
                                    reportType="experian"
                                />
                                <div className="flex justify-content-end mt-3">
                                    <Button type="submit" label="Start Verification" icon="pi pi-arrow-right" iconPos="right" loading={loading} disabled={loading} size="small" />
                                </div>
                            </form>
                        </Card>
                    )}

                    {step === STEPS.SDK && (
                        <Card>
                            <Message severity="info" text="Open the Experian verification SDK in a new tab and complete the verification. Once done, return here and click ‘Fetch Credit Report’." className="w-full mb-3" />
                            <div className="flex flex-column md:flex-row gap-2 align-items-start md:align-items-center justify-content-between mb-3">
                                <div className="flex align-items-center gap-2 min-w-0 flex-1">
                                    <i className="pi pi-link text-primary" aria-hidden="true" />
                                    <a href={sdkUrl} target="_blank" rel="noreferrer noopener" className="text-sm text-primary white-space-nowrap overflow-hidden text-overflow-ellipsis flex-1">
                                        {sdkUrl}
                                    </a>
                                </div>
                                <Button label="Open SDK" icon="pi pi-external-link" size="small" onClick={() => window.open(sdkUrl, '_blank', 'noopener,noreferrer')} />
                            </div>
                            <div className="flex justify-content-between mt-3">
                                <Button label="Restart" icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={handleAnother} />
                                <Button label="Fetch Credit Report" icon="pi pi-download" iconPos="right" loading={loading} disabled={loading} size="small" onClick={fetchReport} />
                            </div>
                        </Card>
                    )}

                    {step === STEPS.REPORT && reportData && (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button label="Generate another" icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={handleAnother} />
                            </div>
                            <ExperianOutputComponent reportData={reportData} serviceDetails={serviceDetails} />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default ExperianInputComponent;
